import { Fragment, useState, type CSSProperties } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { hasRole, reactionClusterId, clusterIdentityTokens, getRootCauseBucket } from '@death-star/shared';
import { useApiQuery } from '../use-api.js';
import { useRole } from '../use-role.js';
import { useAuth } from '../auth.js';
import { ApiError, apiSendJson } from '../api-client.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';

// Sentinel for "all orgs" — empty-string value follows the same convention
// repo-overview uses. The reactions API treats missing/empty ?org= as an
// all-orgs aggregation signal (see api-digest-reactions.ts).
const ALL_ORGS = '';

function isValidOrg(v: string | null, orgs: string[]): v is string {
  if (v === null) return false;
  if (v === ALL_ORGS) return true;
  return orgs.includes(v);
}

const emptyStateBox: CSSProperties = {
  background: 'var(--loop-neutral-100)',
  border: '1px solid var(--loop-neutral-200)',
  borderRadius: 'var(--loop-radius-sm)',
  padding: 'var(--loop-space-300)',
  textAlign: 'center',
};

interface VerdictDistribution {
  auto_approved?: number;
  human_required?: number;
  skipped?: number;
  [key: string]: number | undefined;
}

interface CostSummary {
  total_usd?: number;
  // tokens are optional in the API response — the all-orgs aggregation may or
  // may not include them (summing tokens across orgs is loosely defined), but
  // per-org rows from the producer always carry them.
  top_5_by_cost?: Array<{ org: string; repo: string; pr: number; usd: number; tokens_in?: number; tokens_out?: number }>;
  [key: string]: unknown;
}

interface ReviewerSkipRate {
  org: string;
  repo: string;
  reviewer: string;
  runs: number;
  filter_skipped: number;
  rate: number;
}

interface ReviewerRequestRate {
  org: string;
  repo: string;
  reviewer: string;
  requests: number;
  distinct_authors: number;
}

export interface FailingReviewerRow {
  org: string;
  reviewer: string;
  ok: number;
  timeouts: number;
  errors: number;
  failures?: number;            // # of subprocess crashes (status==='failed'). Optional for backward compat with digests written before 2026-05-15.
  failure_rate: number;
}

import { sortIncidentsThisWeek, type IncidentThisWeek } from './weekly-digest-incidents.js';

interface DigestResponse {
  week: string;
  org: string;
  verdict_distribution?: VerdictDistribution;
  new_authors?: string[];
  cost_summary?: CostSummary;
  reviewer_skip_rates?: ReviewerSkipRate[];
  reviewer_request_rates?: ReviewerRequestRate[];
  failing_reviewers?: FailingReviewerRow[];
  // Per-org only — all-orgs aggregation omits this field (incidents are
  // per-org by definition; merging would double-count cross-org incidents).
  incidentsThisWeek?: IncidentThisWeek[];
}

const SKIP_RATE_THRESHOLD = 0.8;

const inputStyle: CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)',
  height: '36px',
  boxSizing: 'border-box',
};

const selectStyle: CSSProperties = {
  ...inputStyle,
  // appearance:none drops the macOS chrome that ignores the explicit height,
  // making the select visually flush with the week input. A custom chevron
  // is rendered as a sibling element below so the dropdown affordance is
  // unambiguous (a background-image SVG attempt was too faint at 12px).
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  cursor: 'pointer',
  paddingRight: '32px',
};

const selectWrapStyle: CSSProperties = {
  position: 'relative',
  display: 'inline-block',
};

const selectChevronStyle: CSSProperties = {
  position: 'absolute',
  right: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  color: 'var(--loop-neutral-900)',
  display: 'flex',
  alignItems: 'center',
};

function currentIsoWeekMinus1(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  // ISO 8601: shift to the Thursday of the same ISO week (Mon=1..Sun=7),
  // then count weeks from Jan 1 of that Thursday's year. This handles
  // year-boundary cases where ISO week 1/52/53 spans calendar years.
  const dayNum = d.getDay() === 0 ? 7 : d.getDay();
  d.setDate(d.getDate() + 4 - dayNum);
  const yearStart = new Date(d.getFullYear(), 0, 1);
  const weekNum = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getFullYear()}-W${String(weekNum).padStart(2, '0')}`;
}

// ============================================================
// Reaction patterns section — surfaces the weekly digest's
// clustered 👎 reactions per reviewer for EM/TL triage.
// ============================================================

interface ReactionSampleEntry {
  org: string;
  repo: string;
  pr: number;
  comment_id: number;
  message_excerpt: string;
}

// When an EM/TL has actioned this cluster (acknowledge/dismiss/draft_*_pr),
// the API attaches this audit-row summary so the portal can render a
// greyed, annotated row instead of the action buttons. Source of truth is
// the audit row in DynamoDB, so this state survives page reload.
interface ClusterActionedSummary {
  action: 'acknowledge' | 'dismiss' | 'draft_prompt_pr' | 'draft_policy_pr';
  by: string;
  at: string;
  rationale: string;
  pr_url: string | null;
  /** Present for draft_*_pr actions. */
  status?: 'drafting' | 'done' | 'fell_back' | 'failed' | 'no_change';
  /** Present when status === 'done'. */
  llm_rationale?: string;
  /** Present when status === 'fell_back'. */
  fallback_reason?: string;
  /** Present when status === 'no_change'. */
  existing_anchor?: string;
}

interface ReactionClusterEntry {
  title?: string;
  one_line_why?: string;
  cluster_key?: string;
  keyword_tokens: string[];
  sample_message: string;
  sample_pr_link: string;
  down_count: number;
  total_reactions: number;
  // Optional because digest rows written before this field shipped don't carry
  // it. Render the disclosure only when present and non-empty.
  samples?: ReactionSampleEntry[];
  actioned?: ClusterActionedSummary;
  root_cause?: string;
}

function prReviewCommentUrl(s: ReactionSampleEntry): string {
  // GitHub anchors PR review comments at #discussion_r{id}. (Issue-level
  // comments use #issuecomment-{id}, but poll-reactions ingests via
  // reactions.listForPullRequestReviewComment, so every sample here is a
  // review comment.)
  return `https://github.com/${encodeURIComponent(s.org)}/${encodeURIComponent(s.repo)}/pull/${s.pr}#discussion_r${s.comment_id}`;
}


interface ReactionReviewerEntry {
  name: string;
  counts: { up: number; down: number; confused: number };
  ratio_negative: number;
  ratio_negative_prior?: number;
  top_clusters: ReactionClusterEntry[];
  // Populated only when the api-digest-reactions endpoint is called with no
  // org param (all-orgs aggregation). Each card then renders the source org
  // so the EM/TL knows which org's data they're looking at.
  org?: string;
  // Which path assigned this reviewer's buckets. Absent on digest rows written
  // before provenance was recorded — which is every week up to 2026-34, all of
  // which were in fact 'deterministic'.
  classifier?: 'llm' | 'deterministic';
}

interface ReactionData {
  reviewers: ReactionReviewerEntry[];
  unknown_reviewer: { up: number; down: number; confused: number };
  classifier?: 'llm' | 'deterministic';
}

export interface ActionInvocation {
  action: 'acknowledge' | 'dismiss' | 'draft_prompt_pr' | 'draft_policy_pr';
  cluster_id: string;
  reviewer: string;
  week: string;
  rationale: string;
  org?: string;
  tokens?: string[];
  force_retry?: boolean;
  force_draft?: boolean;
}

const REACTION_RATIO_THRESHOLD = 0.15;

// Below this many total reactions, `ratio_negative` is arithmetic, not a rate:
// in week 2026-33 contract-consistency rendered "ratio 100%" off a single 👎 on a
// single reaction, which reads as a reviewer failing every time it speaks.
//
// The reviewer is still surfaced and the cluster is still actionable — a lone 👎
// on a blocking finding was the most valuable signal in that week, so hiding it
// would be strictly worse. Only the misleading PERCENTAGE is withheld, replaced
// by the raw fraction, and the week-over-week delta is suppressed with it (a
// swing between two single-digit denominators is noise).
const REACTION_MIN_DENOMINATOR = 4;

// Past-tense labels for the action verb shown in the actioned annotation.
// dismiss/acknowledge are self-evident; the draft_*_pr variants land in
// a separate location (the linked PR), so the verb wording is "drafted".
const ACTION_VERB_PAST: Record<ClusterActionedSummary['action'], string> = {
  acknowledge: 'Acknowledged',
  dismiss: 'Dismissed (suppressed 90d)',
  draft_prompt_pr: 'Drafted prompt PR',
  draft_policy_pr: 'Drafted policy PR',
};

function formatActionedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Short local format keeps the row from blowing up on long rationales.
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const draftingPillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '3px 10px',
  borderRadius: 999,
  background: 'var(--loop-neutral-200)',
  color: 'var(--loop-neutral-800)',
  fontSize: '0.8rem',
  fontWeight: 528,
  border: '1px solid var(--loop-neutral-300)',
};

const retryBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--loop-blue-700)',
  fontSize: '0.8125rem',
  cursor: 'pointer',
  padding: '0 4px',
  textDecoration: 'underline',
  fontFamily: 'inherit',
};

function SpinnerIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
      style={{ animation: 'spin 1s linear infinite' }}
    >
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  );
}

function ActionedAnnotation({ actioned, onRetry, onDraftAnyway }: { actioned: ClusterActionedSummary; onRetry?: () => void; onDraftAnyway?: () => void }) {
  if (actioned.status === 'drafting') {
    return (
      <span style={draftingPillStyle}>
        <SpinnerIcon />
        Drafting PR…
      </span>
    );
  }

  if (actioned.status === 'no_change') {
    return (
      <span
        style={{ display: 'inline-flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 4, fontSize: '0.8125rem', color: 'var(--loop-neutral-800)' }}
        title={actioned.llm_rationale || actioned.rationale || 'Already handled — no PR needed'}
      >
        <strong style={{ fontWeight: 'var(--loop-font-weight-medium)' }}>Already handled</strong>
        <span style={{ color: 'var(--loop-neutral-600)' }}>— no PR needed</span>
        {actioned.existing_anchor && (
          <span style={{ color: 'var(--loop-neutral-700)', fontStyle: 'italic' }}>
            (covered by {actioned.existing_anchor})
          </span>
        )}
        {onDraftAnyway && (
          <button type="button" style={retryBtnStyle} onClick={onDraftAnyway} title="Override: draft a calibration-note PR even though the model judged this already covered">
            Draft anyway
          </button>
        )}
      </span>
    );
  }

  if (actioned.status === 'failed') {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8125rem', color: 'var(--loop-neutral-800)' }}>
        <span style={{ color: 'var(--loop-red-700)' }}>Drafting failed</span>
        {onRetry && (
          <button type="button" style={retryBtnStyle} onClick={onRetry} title="Re-submit this draft action">
            Retry
          </button>
        )}
      </span>
    );
  }

  const verb = ACTION_VERB_PAST[actioned.action];
  const when = formatActionedAt(actioned.at);
  return (
    <span
      style={{
        fontSize: '0.8125rem',
        color: 'var(--loop-neutral-800)',
        lineHeight: 1.4,
        display: 'inline-flex',
        flexWrap: 'wrap',
        gap: 4,
        alignItems: 'baseline',
      }}
      title={`${actioned.rationale || '(no rationale)'} — by ${actioned.by} on ${actioned.at}`}
    >
      <strong style={{ fontWeight: 'var(--loop-font-weight-medium)' }}>{verb}</strong>
      <span style={{ color: 'var(--loop-neutral-600)' }}>
        by {actioned.by} · {when}
      </span>
      {actioned.rationale && (
        <span style={{ color: 'var(--loop-neutral-700)', fontStyle: 'italic' }}>
          — {actioned.rationale}
        </span>
      )}
      {actioned.pr_url && (
        <a href={actioned.pr_url} target="_blank" rel="noreferrer" style={{ color: 'var(--loop-blue-700)' }}>
          view PR ↗
        </a>
      )}
      {actioned.status === 'fell_back' && (
        <InfoTooltip
          label="LLM fallback"
          text="LLM fallback — used static TODO marker; refine prompt manually"
        />
      )}
    </span>
  );
}

const reactionCardStyle: CSSProperties = {
  border: '1px solid var(--loop-neutral-300)',
  background: 'var(--loop-neutral-0)',
  padding: 'var(--loop-space-300)',
  borderRadius: 'var(--loop-radius-sm)',
  marginBottom: 'var(--loop-space-300)',
};

const clusterGridStyle: CSSProperties = {
  display: 'grid',
  // Fixed label column + auto count + auto buttons + 1fr filler. Each card
  // owns its own grid (so subgrid would be cleaner), but a flat 22rem keeps
  // the label column width identical across cards so the buttons line up
  // visually whether you're looking at one card or scanning down the list.
  gridTemplateColumns: '22rem auto auto 1fr',
  columnGap: 'var(--loop-space-300)',
  alignItems: 'center',
};

const ackBtnStyle: CSSProperties = {
  background: 'var(--loop-neutral-700)',
  color: 'var(--loop-neutral-0)',
  border: 'none',
  borderRadius: 'var(--loop-radius-sm)',
};

const dismissBtnStyle: CSSProperties = {
  background: 'var(--loop-red-600)',
  color: 'var(--loop-neutral-0)',
  border: 'none',
  borderRadius: 'var(--loop-radius-sm)',
};

const promptBtnStyle: CSSProperties = {
  background: 'var(--loop-brand-600)',
  color: 'var(--loop-neutral-0)',
  border: 'none',
  borderRadius: 'var(--loop-radius-sm)',
};

const policyBtnStyle: CSSProperties = {
  background: 'var(--loop-green-600)',
  color: 'var(--loop-neutral-0)',
  border: 'none',
  borderRadius: 'var(--loop-radius-sm)',
};

const reactionCardHeaderStyle: CSSProperties = {
  paddingBottom: 'var(--loop-space-200)',
};

function InfoTooltip({ label, text }: { label: string; text: string }) {
  return (
    <span style={{ position: 'relative', display: 'inline-block', marginLeft: '4px' }}>
      <span
        aria-label={label}
        tabIndex={0}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: 'var(--loop-blue-600)',
          color: 'var(--loop-neutral-0)',
          fontSize: '0.7rem',
          fontWeight: 700,
          fontStyle: 'normal',
          cursor: 'help',
          userSelect: 'none',
        }}
        onMouseEnter={(e) => {
          const tip = e.currentTarget.nextElementSibling as HTMLElement | null;
          if (tip) tip.style.visibility = 'visible';
        }}
        onMouseLeave={(e) => {
          const tip = e.currentTarget.nextElementSibling as HTMLElement | null;
          if (tip) tip.style.visibility = 'hidden';
        }}
        onFocus={(e) => {
          const tip = e.currentTarget.nextElementSibling as HTMLElement | null;
          if (tip) tip.style.visibility = 'visible';
        }}
        onBlur={(e) => {
          const tip = e.currentTarget.nextElementSibling as HTMLElement | null;
          if (tip) tip.style.visibility = 'hidden';
        }}
      >
        i
      </span>
      <span
        role="tooltip"
        style={{
          visibility: 'hidden',
          position: 'absolute',
          top: '24px',
          left: '0',
          zIndex: 10,
          background: 'var(--loop-neutral-1100)',
          color: 'var(--loop-neutral-0)',
          padding: '8px 12px',
          borderRadius: 'var(--loop-radius-sm)',
          fontSize: '0.8rem',
          lineHeight: 1.4,
          width: 'max-content',
          maxWidth: '320px',
          whiteSpace: 'pre-wrap',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          pointerEvents: 'none',
        }}
      >
        {text}
      </span>
    </span>
  );
}

export function ReactionPatternsSection({
  week,
  data,
  onAction,
  org: selectedOrg = '',
  canWrite = false,
}: {
  week: string;
  data: ReactionData;
  onAction: (inv: ActionInvocation) => Promise<void>;
  org?: string;
  canWrite?: boolean;
}) {
  const surfaced = data.reviewers.filter((r) => r.ratio_negative >= REACTION_RATIO_THRESHOLD);
  const unknownTotal =
    data.unknown_reviewer.down + data.unknown_reviewer.up + data.unknown_reviewer.confused;
  if (surfaced.length === 0 && unknownTotal === 0) return null;

  return (
    <section style={{ marginTop: 'var(--loop-space-400)' }}>
      <h2>Reaction patterns · week {week}</h2>
      {data.classifier === 'deterministic' && (
        <div
          role="status"
          style={{
            margin: '0 0 var(--loop-space-300)',
            padding: 'var(--loop-space-200) var(--loop-space-300)',
            border: '1px solid var(--loop-warning-500, #b0741f)',
            borderRadius: 4,
            background: 'var(--loop-warning-100, #f7efe0)',
            color: 'var(--loop-neutral-1100)',
            fontSize: '0.9rem',
          }}
        >
          <strong>Buckets not LLM-classified.</strong> At least one reviewer fell back to the
          keyword bucketer this week, so the root causes below are keyword guesses — measured at
          6 mis-buckets out of 6 when this last happened. Re-cluster the week before acting on a
          root cause, or read the samples directly.
        </div>
      )}
      {surfaced.map((r) => {
        const total = r.counts.up + r.counts.down + r.counts.confused;
        // Key includes org so multi-org all-orgs aggregation gives stable
        // React keys (otherwise two cards both named "audit-security" collide).
        const cardKey = r.org ? `${r.org}/${r.name}` : r.name;
        return (
          <article key={cardKey} style={reactionCardStyle}>
            <div style={reactionCardHeaderStyle}>
              <strong>{r.name}</strong>
              {r.org && (
                <span style={{
                  marginLeft: 8,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: 'var(--loop-neutral-200)',
                  color: 'var(--loop-neutral-700)',
                  fontSize: '0.7rem',
                  fontWeight: 528,
                  fontFamily: 'monospace',
                  verticalAlign: 'middle',
                }}>{r.org}</span>
              )} · {r.counts.up} 👍 / {r.counts.down} 👎 / {r.counts.confused} 😕 ·{' '}
              {total < REACTION_MIN_DENOMINATOR ? (
                <span title={`Only ${total} reaction${total === 1 ? '' : 's'} — too few to express as a rate.`}>
                  {r.counts.down} 👎 of {total}
                  <span style={{ marginLeft: 6, color: '#888' }}>• too few to rate</span>
                </span>
              ) : (
                <>
                  ratio {(r.ratio_negative * 100).toFixed(0)}%
                  {r.ratio_negative_prior !== undefined && (() => {
                    const deltaPts = Math.round((r.ratio_negative - r.ratio_negative_prior!) * 100);
                    if (deltaPts === 0) return <span style={{ marginLeft: 6, color: '#888' }}>• flat vs last wk</span>;
                    const up = deltaPts > 0;
                    return (
                      <span style={{ marginLeft: 6, color: up ? '#c0392b' : '#27ae60' }}>
                        {up ? '▲' : '▼'} {Math.abs(deltaPts)} pts vs last wk
                      </span>
                    );
                  })()}
                </>
              )}
              <InfoTooltip
                label="What do these numbers mean?"
                text={
                  `Reviewer skill '${r.name}' on this week's findings. ` +
                  `${total} total reactions across the findings it produced.\n\n` +
                  (total < REACTION_MIN_DENOMINATOR
                    ? `Only ${total} reaction${total === 1 ? '' : 's'} — fewer than ${REACTION_MIN_DENOMINATOR}, so the 👎 count is shown raw instead of as a percentage. A ratio off this few reactions isn't a rate: one 👎 of one would read as 100%. The clusters below are still worth triaging — a single 👎 on a blocking finding is real signal.\n\n`
                    : `Negative ratio = 👎 ÷ total = ${(r.ratio_negative * 100).toFixed(0)}%. ` +
                      `Reviewers appear here when this ratio is ≥ 15% — the threshold for "this reviewer is producing patterns worth triaging."\n\n`) +
                  `Cluster rows below show recurring 👎 patterns only — that's where action lives. 👍 and 😕 reactions roll up into the header counts and the negative-ratio denominator.`
                }
              />
            </div>
            {r.top_clusters.length === 0 ? (
              <p style={{ color: 'var(--loop-neutral-700)' }}>no recurring pattern surfaced this week</p>
            ) : (
              <div style={clusterGridStyle}>
                {r.top_clusters.map((c, idx) => {
                  // Use selectedOrg as fallback so per-org view (where the API
                  // omits r.org since it's already filtered) still produces an
                  // org-scoped cluster_id. Otherwise the audit-row key
                  // ACTION#<week>#<cluster_id> collides across orgs and the
                  // second org's action 409s with already_actioned.
                  const id = reactionClusterId(r.name, week, clusterIdentityTokens(c), r.org ?? selectedOrg);
                  const actionedStatus = c.actioned?.status;
                  const isFailed = actionedStatus === 'failed';
                  // 'drafting' and terminal actioned states (done/fell_back/acknowledge/dismiss) grey out the row.
                  // 'failed' does not — the user needs to retry.
                  const isActioned = !!c.actioned && !isFailed;
                  const cellStyle: CSSProperties = {
                    paddingTop: 'var(--loop-space-200)',
                    paddingBottom: 'var(--loop-space-200)',
                    display: 'flex',
                    alignItems: 'center',
                    opacity: isActioned ? 0.55 : 1,
                  };
                  return (
                    <Fragment key={id}>
                      {idx > 0 && (
                        <div style={{ gridColumn: '1 / -1', borderTop: '1px solid var(--loop-neutral-400)' }} />
                      )}
                      <span style={{ ...cellStyle, fontWeight: 'var(--loop-font-weight-medium)', textDecoration: isActioned ? 'line-through' : 'none' }}>
                        {c.root_cause && (
                          <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 8, background: '#eef', marginRight: 6 }}
                                title={getRootCauseBucket(c.root_cause)?.description}>
                            {c.root_cause}
                          </span>
                        )}
                        {c.title ?? c.keyword_tokens.join(' ')}
                        {c.one_line_why ? (
                          <span style={{ display: 'block', fontWeight: 'var(--loop-font-weight-regular)', color: 'var(--loop-neutral-700)', fontSize: '0.85em' }}>
                            {c.one_line_why}
                          </span>
                        ) : null}
                      </span>
                      <span style={{ ...cellStyle, color: 'var(--loop-neutral-700)', textDecoration: isActioned ? 'line-through' : 'none' }}>
                        {c.down_count} 👎
                      </span>
                      <div style={{ ...cellStyle, gap: 'var(--loop-space-150)', flexWrap: 'wrap', opacity: 1 }}>
                        {c.actioned ? (
                          <ActionedAnnotation
                            actioned={c.actioned}
                            onRetry={isFailed ? () => {
                              void onAction({ action: c.actioned!.action as 'draft_prompt_pr' | 'draft_policy_pr', cluster_id: id, reviewer: r.name, week, rationale: c.actioned!.rationale, org: r.org ?? selectedOrg, tokens: c.keyword_tokens, force_retry: true })
                                .catch((err) => alert(`Retry failed: ${(err as Error).message}`));
                            } : undefined}
                            onDraftAnyway={actionedStatus === 'no_change' ? () => {
                              void onAction({ action: c.actioned!.action as 'draft_prompt_pr' | 'draft_policy_pr', cluster_id: id, reviewer: r.name, week, rationale: c.actioned!.rationale, org: r.org ?? selectedOrg, tokens: c.keyword_tokens, force_retry: true, force_draft: true })
                                .catch((err) => alert(`Draft anyway failed: ${(err as Error).message}`));
                            } : undefined}
                          />
                        ) : canWrite && (
                          <>
                            <button
                              className="btn-sm"
                              style={ackBtnStyle}
                              title="Record this as handled (e.g. fixed upstream). No future findings suppressed."
                              onClick={() => {
                                void onAction({ action: 'acknowledge', cluster_id: id, reviewer: r.name, week, rationale: '', org: r.org ?? selectedOrg, tokens: c.keyword_tokens })
                                  .catch((err) => alert(`Action failed: ${(err as Error).message}`));
                              }}
                            >
                              Acknowledge
                            </button>
                            <button
                              className="btn-sm"
                              style={dismissBtnStyle}
                              title="Suppress future findings matching this token pattern for 90 days."
                              onClick={() => {
                                void onAction({ action: 'dismiss', cluster_id: id, reviewer: r.name, week, rationale: '', org: r.org ?? selectedOrg, tokens: c.keyword_tokens })
                                  .catch((err) => alert(`Action failed: ${(err as Error).message}`));
                              }}
                            >
                              Dismiss
                            </button>
                            <button
                              className="btn-sm"
                              style={promptBtnStyle}
                              onClick={() => {
                                void onAction({ action: 'draft_prompt_pr', cluster_id: id, reviewer: r.name, week, rationale: '', org: r.org ?? selectedOrg, tokens: c.keyword_tokens })
                                  .catch((err) => alert(`Action failed: ${(err as Error).message}`));
                              }}
                            >
                              Draft prompt PR
                            </button>
                            <button
                              className="btn-sm"
                              style={policyBtnStyle}
                              onClick={() => {
                                void onAction({ action: 'draft_policy_pr', cluster_id: id, reviewer: r.name, week, rationale: '', org: r.org ?? selectedOrg, tokens: c.keyword_tokens })
                                  .catch((err) => alert(`Action failed: ${(err as Error).message}`));
                              }}
                            >
                              Draft policy PR
                            </button>
                          </>
                        )}
                      </div>
                      {c.samples && c.samples.length > 0 && (
                        <div style={{ gridColumn: '1 / -1', paddingTop: 'var(--loop-space-150)' }}>
                          <details>
                            <summary style={{ cursor: 'pointer', fontSize: '0.8125rem', color: 'var(--loop-neutral-700)' }}>
                              View {c.samples.length} sample review {c.samples.length === 1 ? 'comment' : 'comments'} that contributed to this pattern
                            </summary>
                            <ul style={{ margin: '8px 0 0 0', padding: '0 0 0 18px', listStyle: 'disc' }}>
                              {c.samples.map((s) => (
                                <li
                                  key={`${s.org}/${s.repo}#${s.pr}#${s.comment_id}`}
                                  style={{ marginBottom: 6, fontSize: '0.8125rem' }}
                                >
                                  <a
                                    href={prReviewCommentUrl(s)}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{
                                      color: 'var(--loop-blue-700)',
                                      textDecoration: 'none',
                                      fontFamily: 'monospace',
                                    }}
                                  >
                                    {s.org}/{s.repo}#{s.pr}
                                  </a>
                                  <span style={{ color: 'var(--loop-neutral-500)' }}> · </span>
                                  <span style={{ color: 'var(--loop-neutral-800)', fontStyle: 'italic' }}>
                                    {s.message_excerpt}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </details>
                        </div>
                      )}
                    </Fragment>
                  );
                })}
              </div>
            )}
          </article>
        );
      })}
      {unknownTotal > 0 && (
        <p style={{ fontSize: '0.875rem', color: 'var(--loop-neutral-900)' }}>
          🪵 {unknownTotal} reactions on un-attributable findings (older PRs or edited comments).
          <InfoTooltip
            label="What does un-attributable mean?"
            text={
              `Each Diff Vader finding comment carries an HTML-comment marker token like <!-- dv:r=correctness --> at the top, ` +
              `which the ingest reads to know which reviewer skill produced the finding.\n\n` +
              `If that marker is missing — typically because the comment is from an older PR (before this feature shipped) or because ` +
              `someone edited the comment and stripped the marker — the reaction can't be attributed to a specific reviewer. ` +
              `It still gets counted, but it lands in this catch-all bucket instead of feeding the per-reviewer clusters.`
            }
          />
        </p>
      )}
    </section>
  );
}

// ============================================================
// Incidents this week section — Phase 3 EXIT task.
// Surfaces incidents whose occurred_at falls in the digest's
// [weekStart, weekEnd) window AND whose linked PRs touch the
// org being digested. The load-bearing UI is the bot-approved
// callout: any incident with `any_bot_approved_linked_pr` gets
// a prominent red-bordered card so the EM/TL has an explicit
// review item for each bot-auto-approved-PR-touched incident.
// ============================================================

function truncateTitle(s: string, max = 60): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

function ConfluenceMark({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-label="Confluence" role="img">
      <path fill="#2684ff" d="M2.23,49.53c-.65,1.06-1.38,2.29-2,3.27a2,2,0,0,0,.67,2.72l13,8a2,2,0,0,0,2.77-.68c.52-.87,1.19-2,1.92-3.21,5.15-8.5,10.33-7.46,19.67-3l12.89,6.13a2,2,0,0,0,2.69-1l6.19-14a2,2,0,0,0-1-2.62c-2.72-1.28-8.13-3.83-13-6.18C28.51,30.45,13.62,31,2.23,49.53Z" />
      <path fill="#0052cc" d="M60.52,17.76c.65-1.06,1.38-2.29,2-3.27a2,2,0,0,0-.67-2.72l-13-8A2,2,0,0,0,46,4.43c-.52.87-1.19,2-1.92,3.21-5.15,8.5-10.33,7.46-19.67,3L11.56,4.54a2,2,0,0,0-2.69,1l-6.19,14a2,2,0,0,0,1,2.62c2.72,1.28,8.13,3.83,13,6.18C34.24,36.84,49.13,36.27,60.52,17.76Z" />
    </svg>
  );
}

function PagerDutyMark({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-label="PagerDuty" role="img">
      <path fill="#06ac38" d="M6.704 59.217H0v-33.65c0-3.455 1.418-5.544 2.604-6.704 2.63-2.58 6.2-2.656 6.782-2.656h10.546c3.765 0 5.93 1.52 7.117 2.8 2.346 2.553 2.372 5.853 2.32 6.73v12.687c0 3.662-1.496 5.828-2.733 6.988-2.553 2.398-5.93 2.45-6.73 2.424H6.704zm13.46-18.102c.36 0 1.367-.103 1.908-.62.413-.387.62-1.083.62-2.1v-13.02c0-.36-.077-1.315-.593-1.857-.5-.516-1.444-.62-2.166-.62h-10.6c-2.63 0-2.63 1.985-2.63 2.656v15.55zM57.296 4.783H64V38.46c0 3.455-1.418 5.544-2.604 6.704-2.63 2.58-6.2 2.656-6.782 2.656H44.068c-3.765 0-5.93-1.52-7.117-2.8-2.346-2.553-2.372-5.853-2.32-6.73V25.62c0-3.662 1.496-5.828 2.733-6.988 2.553-2.398 5.93-2.45 6.73-2.424h13.202zM43.836 22.9c-.36 0-1.367.103-1.908.62-.413.387-.62 1.083-.62 2.1v13.02c0 .36.077 1.315.593 1.857.5.516 1.444.62 2.166.62h10.598c2.656-.026 2.656-2 2.656-2.682V22.9z" />
    </svg>
  );
}

function IncidentSeverityBadge({ severity }: { severity: string | null }) {
  if (!severity) return null;
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 'var(--loop-radius-xs)',
      background: 'var(--loop-red-100)',
      color: 'var(--loop-red-700)',
      fontSize: '0.6875rem',
      fontWeight: 528,
      marginLeft: 8,
    }}>
      {severity}
    </span>
  );
}

export function IncidentsThisWeekSection({ incidents }: { incidents: IncidentThisWeek[] }) {
  if (!incidents || incidents.length === 0) return null;

  const sorted = sortIncidentsThisWeek(incidents);
  const botApprovedCount = sorted.filter(i => i.any_bot_approved_linked_pr).length;

  return (
    <section style={{ marginTop: 'var(--loop-space-400)' }} aria-label="Incidents this week">
      <h2 style={{ fontSize: '1rem', fontWeight: 664, color: 'var(--loop-neutral-1100)', marginBottom: 12 }}>
        Incidents this week
        {botApprovedCount > 0 && (
          <span style={{
            marginLeft: 10,
            padding: '2px 8px',
            borderRadius: 999,
            background: 'var(--loop-red-100)',
            color: 'var(--loop-red-700)',
            fontSize: '0.6875rem',
            fontWeight: 528,
            verticalAlign: 'middle',
          }}>
            {botApprovedCount} bot-approved review item{botApprovedCount === 1 ? '' : 's'}
          </span>
        )}
      </h2>
      <p style={{ color: 'var(--loop-neutral-600)', fontSize: '0.8rem', marginBottom: 'var(--loop-space-300)' }}>
        Incidents this week whose linked PRs touch this org. Incidents with a bot-auto-approved
        linked PR are flagged for explicit EM/TL review.
      </p>
      {sorted.map((inc) => {
        const flagged = inc.any_bot_approved_linked_pr;
        const cardStyle: CSSProperties = {
          background: 'var(--loop-neutral-0)',
          border: '1px solid var(--loop-neutral-300)',
          // Left border is the load-bearing visual cue for the EM/TL review
          // queue. Red ⇒ at least one linked PR was bot-approved. Don't fill
          // the whole card red — too much visual noise once 10+ are stacked.
          borderLeft: flagged ? '4px solid var(--loop-red-600)' : '1px solid var(--loop-neutral-300)',
          borderRadius: 'var(--loop-radius-sm)',
          padding: 'var(--loop-space-300)',
          marginBottom: 'var(--loop-space-200)',
        };
        return (
          <article key={inc.incident_id} style={cardStyle} data-bot-approved-flag={flagged ? 'true' : 'false'}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ display: 'inline-flex', lineHeight: 1, marginTop: 2 }}>
                {inc.source === 'pagerduty-statuspage' ? <PagerDutyMark /> : <ConfluenceMark />}
              </span>
              <a
                href={inc.source_url}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: 'var(--loop-blue-700)',
                  fontWeight: 528,
                  fontSize: '0.9375rem',
                  textDecoration: 'none',
                  flex: '1 1 auto',
                  minWidth: 0,
                }}
                title={inc.title}
              >
                {truncateTitle(inc.title)}
              </a>
              <IncidentSeverityBadge severity={inc.severity} />
            </div>
            {flagged && (
              <div
                role="alert"
                style={{
                  marginTop: 8,
                  padding: '6px 10px',
                  borderRadius: 'var(--loop-radius-xs)',
                  background: 'var(--loop-red-100)',
                  color: 'var(--loop-red-700)',
                  fontSize: '0.8125rem',
                  fontWeight: 528,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span aria-hidden="true">{'⚠️'}</span>
                Linked PR was bot-approved — explicit EM/TL review item
              </div>
            )}
            <div style={{ marginTop: 8, fontSize: '0.75rem', color: 'var(--loop-neutral-700)' }}>
              {inc.arch_group && <span style={{ marginRight: 12, textTransform: 'capitalize' }}>{inc.arch_group}</span>}
              <span>{new Date(inc.occurred_at).toUTCString()}</span>
            </div>
            <div style={{ marginTop: 10, fontSize: '0.75rem', color: 'var(--loop-neutral-700)' }}>
              <strong style={{ fontWeight: 528 }}>Linked PRs ({inc.linked_prs.length}):</strong>
              {inc.linked_prs.length === 0 && (
                <span style={{ marginLeft: 6, color: 'var(--loop-neutral-500)' }}>none</span>
              )}
              <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
                {inc.linked_prs.map((pr) => (
                  <li key={`${pr.org}/${pr.repo}#${pr.pr_number}`} style={{ marginBottom: 2 }}>
                    <a
                      href={`https://github.com/${encodeURIComponent(pr.org)}/${encodeURIComponent(pr.repo)}/pull/${pr.pr_number}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: 'var(--loop-blue-700)', textDecoration: 'none' }}
                    >
                      {pr.org}/{pr.repo}#{pr.pr_number}
                    </a>
                    <span style={{ marginLeft: 8, color: 'var(--loop-neutral-600)' }}>
                      {pr.confidence}% · {pr.reason}
                    </span>
                    {pr.has_bot_approved_artifact && (
                      <span style={{
                        marginLeft: 6,
                        padding: '0 6px',
                        borderRadius: 'var(--loop-radius-xs)',
                        background: 'var(--loop-red-100)',
                        color: 'var(--loop-red-700)',
                        fontSize: '0.6875rem',
                        fontWeight: 528,
                      }}>
                        bot-approved
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ marginTop: 10 }}>
              <Link
                to="/incidents"
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--loop-blue-700)',
                  textDecoration: 'none',
                }}
              >
                View on Incidents page →
              </Link>
            </div>
          </article>
        );
      })}
    </section>
  );
}

// ============================================================
// Failing reviewers section — surfaces reviewers whose backend
// failure rate (timeouts + errors / total attempts) crossed the
// 10% threshold this week. Treat as prompt-tuning or budget-bump
// candidates. Empty state is a paragraph, not an empty table —
// screen readers announce empty tables as "table with no rows."
// ============================================================
export function FailingReviewersSection({ rows }: { rows: FailingReviewerRow[] }) {
  if (rows.length === 0) {
    return (
      <section aria-labelledby="failing-reviewers-heading" style={{ marginTop: 'var(--loop-space-400)' }}>
        <h2
          id="failing-reviewers-heading"
          style={{ fontSize: '1rem', fontWeight: 664, color: 'var(--loop-neutral-1100)', marginBottom: 12 }}
        >
          Failing reviewers
        </h2>
        <p style={{ background: 'var(--loop-green-100)', color: 'var(--loop-green-700)', padding: 12, borderRadius: 'var(--loop-radius-sm)', fontSize: '0.875rem' }}>
          No reviewers exceeded the 10% failure-rate threshold this week.
        </p>
      </section>
    );
  }
  return (
    <section aria-labelledby="failing-reviewers-heading" style={{ marginTop: 'var(--loop-space-400)' }}>
      <h2
        id="failing-reviewers-heading"
        style={{ fontSize: '1rem', fontWeight: 664, color: 'var(--loop-neutral-1100)', marginBottom: 12 }}
      >
        Failing reviewers
      </h2>
      <p style={{ marginBottom: 12, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>
        Reviewers with at least 10 attempts and failure rate &ge; 10% this week. Treat as prompt-tuning or budget-bump candidates.
      </p>
      <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
        <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
          <tr>
            {['Org', 'Reviewer', 'Failure rate', 'Timeouts', 'Errors', 'Failures', 'OK'].map((h, i) => (
              <th
                key={h}
                style={{ padding: '6px 12px', fontSize: '0.75rem', color: 'var(--loop-neutral-700)', fontWeight: 528, textAlign: i >= 2 ? 'right' : 'left' }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.org}#${r.reviewer}`} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
              <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.org}</td>
              <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.reviewer}</td>
              <td style={{ padding: '6px 12px', textAlign: 'right' }}>{(r.failure_rate * 100).toFixed(1)}%</td>
              <td style={{ padding: '6px 12px', textAlign: 'right' }}>{r.timeouts}</td>
              <td style={{ padding: '6px 12px', textAlign: 'right' }}>{r.errors}</td>
              <td style={{ padding: '6px 12px', textAlign: 'right' }}>{r.failures ?? 0}</td>
              <td style={{ padding: '6px 12px', textAlign: 'right' }}>{r.ok}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function MiniMetric({ name, value, context }: { name: string; value: string; context?: string }) {
  return (
    <div className="card" style={{ marginBottom: 0, minWidth: 180, flex: '0 1 auto' }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)', marginBottom: 4 }}>{name}</div>
      <div style={{ fontSize: '1.5rem', fontWeight: 664, color: 'var(--loop-neutral-1100)', lineHeight: 1.2 }}>{value}</div>
      {context && <div style={{ fontSize: '0.7rem', color: 'var(--loop-neutral-500)', marginTop: 4 }}>{context}</div>}
    </div>
  );
}

const rationaleOverlayStyle: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: 'var(--loop-space-300)',
  zIndex: 200,
  overflowY: 'auto',
};

const rationaleDialogStyle: CSSProperties = {
  background: 'var(--loop-neutral-0)',
  borderRadius: 'var(--loop-radius-md)',
  padding: 'var(--loop-space-300)',
  width: '100%',
  maxWidth: 520,
  marginTop: 'var(--loop-space-300)',
  boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
};

// 'acknowledge' = audit-only ("I handled this elsewhere"); cluster can
// resurface next week if the upstream fix didn't take.
// 'dismiss' = audit + 90-day suppression keyed on (reviewer, tokens).
// Both require a rationale on the server (api-reaction-action.ts).
function RationaleModal({
  pending,
  onConfirm,
  onCancel,
}: {
  pending: ActionInvocation;
  onConfirm: (rationale: string) => Promise<void>;
  onCancel: () => void;
}) {
  const [rationale, setRationale] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAck = pending.action === 'acknowledge';
  const title = isAck ? 'Acknowledge pattern' : 'Dismiss pattern';
  const explainer = isAck
    ? 'Record this finding as handled (e.g. fixed upstream). No future findings will be suppressed — the pattern can still resurface next week if the fix did not take.'
    : `Suppress future findings from reviewer "${pending.reviewer}" matching tokens [${(pending.tokens ?? []).map((t) => `"${t}"`).join(', ')}] for the next 90 days.`;
  const trimmed = rationale.trim();
  const canConfirm = trimmed.length > 0 && trimmed.length <= 500 && !saving;

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      await onConfirm(trimmed);
    } catch (e) {
      setError(e instanceof ApiError ? e.body : (e as Error).message);
      setSaving(false);
    }
  }

  const titleId = 'rationale-modal-title';
  const textareaId = 'rationale-modal-textarea';

  return (
    <div style={rationaleOverlayStyle} onClick={(e) => { if (e.target === e.currentTarget && !saving) onCancel(); }}>
      <div style={rationaleDialogStyle} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h3 id={titleId} style={{ margin: '0 0 var(--loop-space-150)', fontSize: '1rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>
          {title}
        </h3>
        <p style={{ fontSize: '0.8125rem', color: 'var(--loop-neutral-800)', lineHeight: 1.5, marginBottom: 'var(--loop-space-200)' }}>
          {explainer}
        </p>
        <label htmlFor={textareaId} style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 'var(--loop-font-weight-medium)', marginBottom: 'var(--loop-space-100)' }}>
          Rationale <span style={{ color: 'var(--loop-red-700)' }}>*</span>
        </label>
        <textarea
          id={textareaId}
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder={isAck ? 'e.g. fixed upstream in loop skill — added component list (PR #123)' : 'e.g. reviewer is over-firing on this token; safe to suppress'}
          style={{
            width: '100%',
            padding: 'var(--loop-space-150)',
            border: '1px solid var(--loop-neutral-400)',
            borderRadius: 'var(--loop-radius-sm)',
            fontFamily: 'inherit',
            fontSize: '0.875rem',
            resize: 'vertical',
            boxSizing: 'border-box',
          }}
          autoFocus
          disabled={saving}
        />
        <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)', marginTop: 4 }}>
          {trimmed.length}/500
        </div>
        {error && (
          <div style={{ color: 'var(--loop-red-700)', fontSize: '0.8rem', marginTop: 'var(--loop-space-150)', padding: 'var(--loop-space-100)', background: 'var(--loop-red-100)', borderRadius: 'var(--loop-radius-xs)' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', gap: 'var(--loop-space-100)', justifyContent: 'flex-end', marginTop: 'var(--loop-space-200)' }}>
          <button className="btn-secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={() => { void submit(); }} disabled={!canConfirm}>
            {saving ? 'Working…' : isAck ? 'Acknowledge' : 'Dismiss'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function WeeklyDigest() {
  const [week, setWeek] = useState(currentIsoWeekMinus1());
  const [searchParams, setSearchParams] = useSearchParams();
  const apiWeek = week.replace('-W', '-');
  const queryClient = useQueryClient();
  const { idToken } = useAuth();
  const { role, loading: roleLoading } = useRole();
  const canWrite = !roleLoading && hasRole(role, 'triager');

  const enterpriseOrgs = useApiQuery<{ orgs: Array<{ login: string; opted_in: boolean }> }>(
    ['enterprise-orgs'],
    '/api/enterprise-orgs'
  );
  const orgOptions: string[] = (enterpriseOrgs.data?.orgs ?? []).map((o) => o.login).sort();
  // Default to "All orgs" when there's no ?org= in the URL. Previously this
  // fell back to orgOptions[0], which could land on an empty org (e.g. a
  // newly-installed account with no activity yet) and show no data on the
  // first page visit. All-orgs is the better landing — it surfaces whatever
  // signal exists across every installed+opted-in org. Once the user picks
  // a specific org from the dropdown, that choice persists via ?org=.
  const fallbackOrg = ALL_ORGS;

  const rawOrg = searchParams.get('org');
  const org: string = isValidOrg(rawOrg, orgOptions) ? rawOrg : fallbackOrg;

  function setOrg(newOrg: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('org', newOrg);
      return next;
    });
  }

  const isAllOrgs = org === ALL_ORGS;
  // For all-orgs the backend aggregates across orgs when ?org= is omitted;
  // sending an explicit empty string would also work but trimming the param
  // keeps the URL clean in the network tab.
  const digestUrl = isAllOrgs
    ? `/api/digest?week=${encodeURIComponent(apiWeek)}`
    : `/api/digest?week=${encodeURIComponent(apiWeek)}&org=${encodeURIComponent(org)}`;
  const { data, isLoading, error } = useApiQuery<DigestResponse>(
    ['digest', apiWeek, org],
    digestUrl
  );

  const reactionsUrl = isAllOrgs
    ? `/api/digest/reactions?week=${encodeURIComponent(apiWeek)}`
    : `/api/digest/reactions?week=${encodeURIComponent(apiWeek)}&org=${encodeURIComponent(org)}`;
  const reactionData = useApiQuery<ReactionData>(
    ['digest-reactions', apiWeek, org],
    reactionsUrl,
    {
      refetchInterval: (q: { state: { data: ReactionData | undefined } }) => {
        const hasDrafting = (q.state.data?.reviewers ?? []).some(
          (r) => r.top_clusters.some((c) => (c.actioned as ClusterActionedSummary | undefined)?.status === 'drafting')
        );
        return hasDrafting ? 3000 : false;
      },
    }
  );

  // Pending ack/dismiss waiting on rationale from the modal. draft_*_pr
  // actions bypass this entirely — the PR they open is the artifact, so
  // we fire those directly without a modal step.
  const [pendingAction, setPendingAction] = useState<ActionInvocation | null>(null);

  async function submitReactionAction(inv: ActionInvocation): Promise<void> {
    // apiSendJson handles apiBase + auth header + ApiError surfacing. The
    // previous bare fetch('/api/...') hit the portal CloudFront (which has
    // an S3 SPA fallback that rewrites 404→200 /index.html), causing every
    // call here to choke parsing '<!doctype' as JSON.
    const body = await apiSendJson<{ ok: true; action: string; pr_url: string | null }>(
      '/api/digest/reaction-action',
      'POST',
      idToken!,
      inv,
    );
    if (body.pr_url) window.open(body.pr_url, '_blank');
    // Invalidate reactions digest so the actioned cluster greys out without
    // resetting the selected week (which window.location.reload() would do).
    await queryClient.invalidateQueries({ queryKey: ['digest-reactions'] });
  }

  async function handleReactionAction(inv: ActionInvocation): Promise<void> {
    if (inv.action === 'acknowledge' || inv.action === 'dismiss') {
      setPendingAction(inv);
      return;
    }
    try {
      await submitReactionAction(inv);
    } catch (err) {
      alert(`Action failed: ${err instanceof ApiError ? err.body : (err as Error).message}`);
    }
  }

  function fmtUsd(n: number | undefined): string {
    if (n === undefined || n === null) return '—';
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>Weekly Digest</h2>
        <ConceptHelpIcon slug="weekly-digest" />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 4, alignItems: 'flex-end' }}>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>Week</label>
          <input
            type="week"
            value={week}
            onChange={e => setWeek(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: 4, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>Org</label>
          <div style={selectWrapStyle}>
            <select
              value={org}
              onChange={e => setOrg(e.target.value)}
              style={selectStyle}
              disabled={enterpriseOrgs.isLoading}
            >
              <option value={ALL_ORGS}>All orgs</option>
              {orgOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
            <span style={selectChevronStyle} aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </span>
          </div>
        </div>
      </div>
      <div style={{ marginBottom: 24 }} />

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error && (() => {
        const is404 = error instanceof Error && /HTTP\s+404/.test(error.message);
        if (is404) {
          return (
            <div style={emptyStateBox}>
              <p style={{ fontWeight: 600 }}>No digest for week {apiWeek}.</p>
              <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>
                The weekly digest aggregator runs Mondays at 08:00 UTC and produces a
                digest for the prior week. If you&apos;re looking at a future or empty
                week, check back after that cron tick — or pick a different week.
              </p>
            </div>
          );
        }
        return <p className="error">Error loading digest: {error instanceof Error ? error.message : String(error)}</p>;
      })()}

      {data && (
        <>
          {/* Change A: Top metric row */}
          {(() => {
            const dist = data.verdict_distribution ?? {};
            const total = Object.values(dist).reduce<number>((a, b) => a + (b ?? 0), 0);
            const approveCount = dist.APPROVE ?? 0;
            const approveRate = total > 0 && data.verdict_distribution
              ? `${Math.round((approveCount / total) * 100)}%`
              : '—';
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
                <MiniMetric name="PRs reviewed" value={String(total)} context="this week" />
                <MiniMetric name="Auto-approve rate" value={approveRate} context="of reviewed PRs" />
                <MiniMetric name="Reviewer cost" value={fmtUsd(data.cost_summary?.total_usd as number | undefined)} context="this week" />
                <MiniMetric name="New authors" value={String((data.new_authors ?? []).length)} context="first PRs" />
              </div>
            );
          })()}

          {/* Change B: Verdict Distribution as stacked bar */}
          <div className="card">
            <h3 style={{ marginBottom: 12, fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)' }}>Verdict Distribution</h3>
            {(() => {
              const dist = data.verdict_distribution ?? {};
              const total = Object.values(dist).reduce<number>((a, b) => a + (b ?? 0), 0);
              const segments: Array<{ key: string; count: number; color: string }> = [
                { key: 'APPROVE', count: dist.APPROVE ?? 0, color: 'var(--loop-green-600)' },
                // Loop only exposes yellow-100 (very pale) and yellow-800 (deep
                // brown) — neither reads as 'yellow' at glance. Hardcoded mid-yellow
                // matching the green-600 / red-600 visual weight until Loop gains
                // a yellow-600 token.
                { key: 'NEEDS_HUMAN', count: dist.NEEDS_HUMAN ?? 0, color: '#EAB308' },
                { key: 'REQUEST_CHANGES', count: dist.REQUEST_CHANGES ?? 0, color: 'var(--loop-red-600)' },
                { key: 'TOO_LARGE', count: dist.TOO_LARGE ?? 0, color: 'var(--loop-neutral-500)' },
              ];
              if (total === 0) {
                return <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>No PRs reviewed this week.</p>;
              }
              return (
                <>
                  <div style={{ display: 'flex', height: 12, borderRadius: 'var(--loop-radius-sm)', overflow: 'hidden', marginBottom: 12 }}>
                    {segments.map((s) => s.count > 0 && (
                      <div key={s.key} style={{ width: `${(s.count / total) * 100}%`, background: s.color }} title={`${s.key}: ${s.count}`} />
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: '0.8rem' }}>
                    {segments.map((s) => (
                      <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ display: 'inline-block', width: 10, height: 10, background: s.color, borderRadius: 2 }} />
                        <span style={{ color: 'var(--loop-neutral-700)' }}>{s.key}</span>
                        <span style={{ fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>{s.count}</span>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Change C: New Authors as chip cloud */}
          <div className="card">
            <h3 style={{ marginBottom: 12, fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)' }}>New Authors</h3>
            {(data.new_authors ?? []).length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(() => {
                  // ISO week → Monday/Sunday date range for GitHub PR search.
                  // Hoisted out of the map — identical per-chip, depends only
                  // on apiWeek + data.org.
                  const [yearStr, weekStr] = apiWeek.split('-');
                  const jan4 = new Date(Date.UTC(parseInt(yearStr!, 10), 0, 4));
                  const monday = new Date(jan4);
                  monday.setUTCDate(
                    jan4.getUTCDate() - ((jan4.getUTCDay() + 6) % 7) + (parseInt(weekStr!, 10) - 1) * 7
                  );
                  const sunday = new Date(monday);
                  sunday.setUTCDate(monday.getUTCDate() + 6);
                  const weekStartIso = monday.toISOString().slice(0, 10);
                  const weekEndIso = sunday.toISOString().slice(0, 10);
                  return data.new_authors!.map((a) => {
                  // Opens GitHub's PR search filtered to this author + org + the
                  // week's merge window. The portal has no internal
                  // author-filterable PR list today (RepoDashboard filters by
                  // repo, not author), so we drill out to github.com.
                  // In all-orgs mode we omit org: so the search spans every org
                  // the viewer can access (rather than the literal "all" sentinel).
                  const orgQualifier = isAllOrgs ? '' : `+org:${encodeURIComponent(data.org)}`;
                  const ghQuery = `is:pr+author:${encodeURIComponent(a)}${orgQualifier}+merged:${weekStartIso}..${weekEndIso}`;
                  const ghUrl = `https://github.com/search?q=${ghQuery}&type=pullrequests`;
                  const scopeLabel = isAllOrgs ? 'across all orgs' : `in ${data.org}`;
                  return (
                    <a
                      key={a}
                      href={ghUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={`See ${a}'s merged PRs ${scopeLabel} for week ${apiWeek}`}
                      className="author-chip"
                      style={{
                        background: 'var(--loop-neutral-200)',
                        color: 'var(--loop-brand-600)',
                        padding: '4px 10px',
                        borderRadius: 999,
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        border: '1px solid var(--loop-neutral-300)',
                        transition: 'background 0.1s, border-color 0.1s',
                      }}
                    >
                      {a}
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M7 17 17 7" />
                        <path d="M7 7h10v10" />
                      </svg>
                    </a>
                  );
                  });
                })()}
              </div>
            ) : (() => {
              // `new_authors` is empty in two distinct cases: (a) PRs were
              // reviewed this week but everyone authoring them had contributed
              // before, or (b) no PRs were reviewed at all. The producer always
              // populates verdict_distribution so its total is a reliable
              // proxy for "any activity this week."
              const totalReviewed = Object.values(data.verdict_distribution ?? {}).reduce<number>((s, n) => s + (n ?? 0), 0);
              const message = totalReviewed > 0
                ? 'No first-time contributors this week — every PR author had contributed before.'
                : 'No PRs reviewed this week.';
              return (
                <div style={{ background: 'var(--loop-green-100)', color: 'var(--loop-green-700)', padding: 12, borderRadius: 'var(--loop-radius-sm)', fontSize: '0.875rem' }}>
                  {message}
                </div>
              );
            })()}
          </div>

          {/* Change D: Cost Summary */}
          <div className="card">
            <h3 style={{ marginBottom: 12, fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)' }}>Cost</h3>
            {data.cost_summary ? (
              <>
                <p style={{ marginBottom: 12, fontSize: '0.875rem' }}>
                  Spent this week:{' '}
                  <span style={{ fontWeight: 664, fontSize: '1.125rem' }}>{fmtUsd(data.cost_summary.total_usd as number)}</span>
                </p>
                {(data.cost_summary.top_5_by_cost ?? []).length > 0 && (
                  <>
                    <h4 style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)', marginBottom: 8, fontWeight: 528, textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 8px' }}>Top PRs by cost</h4>
                    <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                      <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
                        <tr>
                          {['Org', 'Repo', 'PR', 'Cost'].map((h, i) => (
                            <th
                              key={h}
                              style={{ padding: '6px 12px', fontSize: '0.75rem', color: 'var(--loop-neutral-700)', fontWeight: 528, textAlign: i === 3 ? 'right' : 'left' }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {data.cost_summary.top_5_by_cost!.map(p => (
                          <tr key={`${p.org}/${p.repo}/${p.pr}`} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                            <td style={{ padding: '6px 12px' }}>{p.org}</td>
                            <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{p.repo}</td>
                            <td style={{ padding: '6px 12px' }}>#{p.pr}</td>
                            <td style={{ padding: '6px 12px', textAlign: 'right' }}>{fmtUsd(p.usd)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </>
            ) : (
              <p style={{ color: 'var(--loop-neutral-500)', fontSize: '0.875rem' }}>No cost data.</p>
            )}
          </div>

          {/* Change E: Filter Skip Rate with healthy empty state */}
          <div className="card">
            <h3 style={{ marginBottom: 8, fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              Filter Skip Rate{' '}
              <span style={{ color: 'var(--loop-neutral-600)', fontWeight: 400, fontSize: '0.75rem' }}>
                (≥ {Math.round(SKIP_RATE_THRESHOLD * 100)}% of runs)
              </span>
              <InfoTooltip
                label="What is filter skip rate?"
                text={
                  `Each reviewer skill (correctness, audit-security, loop-design-system, etc.) ` +
                  `has an optional filter that runs BEFORE the LLM call to decide whether the reviewer ` +
                  `applies to this PR. Examples: loop-design-system only runs on PRs touching .tsx/.css; ` +
                  `backup-correctness only on engine repos.\n\n` +
                  `Rows where a reviewer has NEVER produced a non-skipped run in this repo across all ` +
                  `stored weekly digests are hidden — those are structural ("reviewer doesn't apply to ` +
                  `this repo, ever") and would otherwise drown out real signal. Surviving rows mean the ` +
                  `reviewer HAS run here before, so a 100% skip this week is a calibration drift worth ` +
                  `looking at.\n\n` +
                  `To debug an apparent false-positive, open the reviewer's SKILL.md in engineering-plugins ` +
                  `(plugins/diff-vader/skills/pr-reviewer-<name>/SKILL.md), inspect the filter logic, ` +
                  `and test it against a few PRs from this repo. Filters live in YAML/JS within the skill.`
                }
              />
            </h3>
            <p style={{ marginBottom: 12, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>
              Reviewers whose runtime filter declines to run on most PRs in a repo.
            </p>
            {(() => {
              const rows = (data.reviewer_skip_rates ?? []).filter((r) => r.rate >= SKIP_RATE_THRESHOLD);
              if (rows.length === 0) {
                return (
                  <div style={{ background: 'var(--loop-green-100)', color: 'var(--loop-green-700)', padding: 12, borderRadius: 'var(--loop-radius-sm)', fontSize: '0.875rem' }}>
                    All reviewers running normally — no filter is silently declining most PRs.
                  </div>
                );
              }
              return (
                <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                  <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
                    <tr>
                      {/* Org first so all-orgs aggregation surfaces the source
                          org immediately; (Repo, Reviewer) can collide across
                          orgs and was the React-key collision bug. */}
                      {['Org', 'Repo', 'Reviewer', 'Runs', 'Skipped', 'Rate'].map((h, i) => (
                        <th
                          key={h}
                          style={{ padding: '6px 12px', fontSize: '0.75rem', color: 'var(--loop-neutral-700)', fontWeight: 528, textAlign: i >= 3 ? 'right' : 'left' }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={`${r.org}/${r.repo}/${r.reviewer}`} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                        <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.org}</td>
                        <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.repo}</td>
                        <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.reviewer}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right' }}>{r.runs}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right' }}>{r.filter_skipped}</td>
                        <td style={{ padding: '6px 12px', textAlign: 'right' }}>{Math.round(r.rate * 100)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>

          {/* On-Demand Request Rate: hidden when empty. With the default
              council including most reviewers, requests via /diff-vader-review
              will be rare — the zero-case shouldn't take any screen space.
              When there ARE requests, the table is real signal worth seeing. */}
          {(data.reviewer_request_rates ?? []).length > 0 && (
            <div className="card">
              <h3 style={{ marginBottom: 8, fontSize: '0.875rem', fontWeight: 664, color: 'var(--loop-neutral-900)' }}>
                On-Demand Request Rate
              </h3>
              <p style={{ marginBottom: 12, color: 'var(--loop-neutral-600)', fontSize: '0.75rem' }}>
                Reviewers requested via <span style={{ fontFamily: 'monospace' }}>/diff-vader-review</span>. High counts on a repo suggest the default council should include this reviewer.
              </p>
              <table style={{ minWidth: '100%', fontSize: '0.875rem', borderCollapse: 'collapse' }}>
                <thead style={{ textAlign: 'left', background: 'var(--loop-neutral-200)' }}>
                  <tr>
                    {['Org', 'Repo', 'Reviewer', 'Requests', 'Authors'].map((h, i) => (
                      <th
                        key={h}
                        style={{ padding: '6px 12px', fontSize: '0.75rem', color: 'var(--loop-neutral-700)', fontWeight: 528, textAlign: i >= 3 ? 'right' : 'left' }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.reviewer_request_rates!.map((r) => (
                    <tr key={`${r.org}/${r.repo}/${r.reviewer}`} style={{ borderTop: '1px solid var(--loop-neutral-200)' }}>
                      <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.org}</td>
                      <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.repo}</td>
                      <td style={{ padding: '6px 12px', fontFamily: 'monospace', fontSize: '0.75rem' }}>{r.reviewer}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right' }}>{r.requests}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right' }}>{r.distinct_authors}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

        </>
      )}

      {data && (
        <FailingReviewersSection rows={data.failing_reviewers ?? []} />
      )}

      {data?.incidentsThisWeek && (
        <IncidentsThisWeekSection incidents={data.incidentsThisWeek} />
      )}

      {reactionData.data && (
        <ReactionPatternsSection week={apiWeek} data={reactionData.data} onAction={handleReactionAction} org={org} canWrite={canWrite} />
      )}

      {pendingAction && (
        <RationaleModal
          pending={pendingAction}
          onCancel={() => setPendingAction(null)}
          onConfirm={async (rationale) => {
            await submitReactionAction({ ...pendingAction, rationale });
            setPendingAction(null);
          }}
        />
      )}
    </div>
  );
}
